/** Normalized model the DealHuntProductCard renders, with mappers from the
 *  DealHunt shapes that feed grids: search hits and category hits. */
import { brandedTitle, categoryLeafLabel } from 'lib/dealhunt/url'
import type {
  DealHuntCategoryHit,
  DealHuntCompareSpec,
  DealHuntMatchedSpec,
  DealHuntSearchHit,
  DealHuntSponsor,
  OwnerCardOwners,
  SgBadge,
} from 'lib/dealhunt/types'

/** Display text for a matched spec pill: the field label for booleans (e.g.
 *  "Fast Charging"), otherwise the matched value + unit (e.g. "USB-C", "40 mm"). */
function formatMatchedSpec(spec: DealHuntMatchedSpec): string {
  if (spec.dataType === 'boolean') {
    return spec.label
  }
  const unit = spec.unit ? ` ${spec.unit}` : ''
  return `${spec.value}${unit}`
}

/** A matched SoundGuys score axis (category/v3 `score.*` filter), rendered by the
 *  card as a mini ScoreRing gauge (the PDP's visual language) instead of a text
 *  pill — a bare "Sound 4.9" reads as out-of-10 next to the /10 SoundGuys score. */
export interface MatchedRating {
  key: string
  label: string
  value: number
  /** Fixed scale ceiling for the ring ("4.9 /5"): 5 for MDAQS axes, 10 otherwise. */
  scaleMax: number
}

function isScoreChip(spec: DealHuntMatchedSpec): boolean {
  return spec.dataType === 'score' || spec.key.startsWith('score.')
}

/** Chip → ring model. `scaleMax` falls back to the axis-key rule (mirrors DealHunt's
 *  `scaleFor`: mdaqs_* is a 1–5 scale, everything else 0–10) until the DealHunt
 *  deploy that stamps `scaleMax` on score chips is live. */
function chipToMatchedRating(spec: DealHuntMatchedSpec): MatchedRating | null {
  const value = typeof spec.value === 'number' ? spec.value : Number(spec.value)
  if (!Number.isFinite(value)) {
    return null
  }
  const axis = spec.key.replace(/^score\./, '')
  return {
    key: spec.key,
    label: spec.label,
    value,
    scaleMax: spec.scaleMax ?? (axis.startsWith('mdaqs_') ? 5 : 10),
  }
}

/** Category subset needed to label a hit (the page's direct subcategories). */
type SubcategoryRef = { fullSlug: string; name: string }

/**
 * Fallback leaf-category resolver for category hits that lack a ready-made
 * `categoryLabel` (migration doc A1, branch-ready): match the hit's `fullSlug`
 * against the page's subcategory slugs (a hit's slug is always prefixed by its
 * subcategory's slug). Superseded by `hit.categoryLabel` once DealHunt deploys it.
 */
function categoryLabelFromSlug(
  fullSlug: string | null,
  subcategories?: ReadonlyArray<SubcategoryRef>
): string | null {
  if (!fullSlug || !subcategories?.length) {
    return null
  }
  const match = subcategories.find(
    s => fullSlug === s.fullSlug || fullSlug.startsWith(`${s.fullSlug}/`)
  )
  return match?.name ?? null
}

/** The title a card renders (and analytics reports): the curated display name, else "Brand Name". */
export function cardTitle(card: Pick<ProductCardModel, 'displayName' | 'brand' | 'title'>): string {
  return card.displayName ?? brandedTitle(card.brand, card.title)
}

export interface ProductCardModel {
  key: string
  fullSlug: string | null
  asin: string
  domain: string
  shortIoUrl: string | null
  title: string
  /** Canonical DealHunt-supplied "{brand} {name}" title. When present the card renders it
   *  verbatim; otherwise it falls back to `brandedTitle(brand, title)`. Migration doc A2. */
  displayName: string | null
  imageUrl: string | null
  brand: string | null
  /** Canonical brand slug for the PDP href (avoids slugifying the display name). Forward-compat
   *  from DealHunt; null falls back to `slugify(brand)`. */
  brandSlug: string | null
  /** Grey leaf-category line shown above the title (replaces the old brand line). */
  categoryLabel: string | null
  currentPriceCents: number | null
  usedPriceCents: number | null
  usedCondition: string | null
  // SoundGuys review (category cards): "Read our review" link + overall score chip
  reviewUrl: string | null
  /** Overall SoundGuys editorial score (0–10), or null when not reviewed/unavailable. */
  reviewScore: number | null
  /** The review's bottom line (category grid cards, contract C0), trimmed; null when empty or
   *  off the category grid (search hits, the PDP rail). The card shows it only where no Owners block renders. */
  reviewOverview: string | null
  /** Active spec filters this product matched (display strings); empty when none.
   *  Score-axis chips are split out into `matchedRatings` — they render as ring
   *  gauges, not text pills. */
  matchedSpecs: string[]
  /** Active SoundGuys score filters this product matched (mini ring gauges); empty
   *  when none. */
  matchedRatings: MatchedRating[]
  /** "Released May 2025" pill echoing what the release-date sort ordered by —
   *  populated only while that sort is active, so the user can eyeball its
   *  accuracy (mirrors how `matchedSpecs` echoes active spec filters). Null when
   *  the sort is inactive or the line is undated: no pill, never a placeholder. */
  releaseDateLabel: string | null
  /** SoundGuys editorial badge (Editor's Choice / Recommended), or null. */
  badge: SgBadge | null
  /** Owner notes on the card (owner-notes contract #66): counts only; null when none or
   *  when the list doesn't carry owner data (search hits, PDP variants). */
  owners: OwnerCardOwners | null
  /** Per-axis numbers for the ± deltas against the reader's declared product (#63):
   *  category cards only; null elsewhere. */
  axes: Record<string, number> | null
  batteryHours: number | null
  /** SoundGuys lab id (v3 cards) — the "versus yours" sparklines fetch curves by it. */
  clearbuyProductId: number | null
  /** Comparable specs (ticket §A); null until DealHunt ships them or off category cards. */
  compareSpecs: DealHuntCompareSpec[] | null
  /** Translation at-a-glance row (AI Translation Earbuds); null on every hit without a
   *  `translation.*` spec, so the row never renders on other categories. */
  translation: TranslationGlance | null
  /** The category grid's sponsored slot (contract C1): who sponsors it + the sponsored post
   *  (analytics). Null on every other card — search hits, the PDP rail, unsponsored lines. */
  sponsored: CardSponsored | null
}

export interface CardSponsored {
  sponsorName: string | null
  postId: number
}

/**
 * "Released May 2025" from category/v2's ISO `releaseDate`. Null in, null out —
 * undated lines (coverage is still backfilling, Jul 2026) simply show no pill —
 * and a malformed date degrades to null rather than "Invalid Date" on a card.
 * Month granularity on purpose: the underlying MIN(listed_since) is a listing
 * proxy that can shift as variants backfill, so day precision would overclaim.
 */
export function formatReleaseDate(iso: string | null | undefined): string | null {
  if (!iso) {
    return null
  }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  // UTC pinned: the label is computed on the server AND re-computed on hydration,
  // so a timezone-dependent month would be a hydration mismatch waiting to happen.
  return `Released ${date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })}`
}

export function hitToCardModel(hit: DealHuntSearchHit): ProductCardModel {
  return {
    key: hit.objectID,
    fullSlug: hit.fullSlug,
    asin: hit.asin,
    domain: hit.domain,
    shortIoUrl: hit.shortIoUrl ?? null,
    title: hit.canonicalName || hit.title,
    displayName: hit.displayName ?? null,
    imageUrl: hit.cloudinaryImageUrl || hit.imageUrl,
    brand: hit.brand,
    brandSlug: hit.brandSlug ?? null,
    categoryLabel: categoryLeafLabel(
      hit['publicCategoryPath.lvl3']?.[0] ??
        hit['publicCategoryPath.lvl2']?.[0] ??
        hit['publicCategoryPath.lvl1']?.[0]
    ),
    currentPriceCents: hit.currentPriceCents,
    usedPriceCents: hit.usedOfferPriceCents,
    usedCondition: hit.usedOfferCondition ?? hit.buyBoxCondition,
    reviewUrl: hit.sgReviewUrl ?? null,
    reviewScore: hit.sgReviewScore ?? null,
    reviewOverview: null,
    matchedSpecs: [],
    matchedRatings: [],
    releaseDateLabel: null,
    badge: hit.sgBadge ?? null,
    owners: null,
    axes: null,
    batteryHours: null,
    clearbuyProductId: null,
    compareSpecs: null,
    translation: null,
    sponsored: null,
  }
}

export function categoryHitToCardModel(
  hit: DealHuntCategoryHit,
  subcategories?: ReadonlyArray<SubcategoryRef>,
  opts?: {
    /** Set while the release-date sort is active; undated hits still get null. */
    showReleaseDate?: boolean
    /** The category grid only (contract C0): map `sgReviewOverview` into `reviewOverview`.
     *  The PDP "More <category>" rail maps the same v2 hits and must stay unchanged. */
    bottomLine?: boolean
    /** Set on the one card built from the v3 `sponsoredSlot` (contract C1). */
    sponsor?: DealHuntSponsor | null
  }
): ProductCardModel {
  return {
    key: hit.objectID,
    fullSlug: hit.fullSlug,
    asin: hit.asin,
    domain: hit.domain,
    shortIoUrl: hit.shortIoUrl,
    title: hit.canonicalName || hit.title,
    displayName: hit.displayName ?? null,
    imageUrl: hit.cloudinaryImageUrl || hit.imageUrl,
    brand: hit.brand,
    brandSlug: hit.brandSlug ?? null,
    categoryLabel: hit.categoryLabel ?? categoryLabelFromSlug(hit.fullSlug, subcategories),
    currentPriceCents: hit.currentPriceCents,
    usedPriceCents: null,
    usedCondition: null,
    reviewUrl: hit.sgReviewUrl ?? null,
    reviewScore: hit.sgReviewScore ?? null,
    reviewOverview: opts?.bottomLine ? hit.sgReviewOverview?.trim() || null : null,
    matchedSpecs: (hit.matchedSpecs ?? []).filter(c => !isScoreChip(c)).map(formatMatchedSpec),
    matchedRatings: (hit.matchedSpecs ?? [])
      .filter(isScoreChip)
      .map(chipToMatchedRating)
      .filter((r): r is MatchedRating => r !== null),
    releaseDateLabel: opts?.showReleaseDate ? formatReleaseDate(hit.releaseDate) : null,
    badge: hit.sgBadge ?? null,
    owners: hit.owners ?? null,
    axes: hit.axes ?? null,
    batteryHours: hit.batteryHours ?? null,
    clearbuyProductId: hit.clearbuyProductId ?? null,
    compareSpecs: hit.compareSpecs ?? null,
    translation: translationGlance(hit.compareSpecs, hit.keySpecs),
    sponsored: opts?.sponsor
      ? { sponsorName: opts.sponsor.name, postId: opts.sponsor.postId }
      : null,
  }
}

// ── Translation at a glance ──────────────────────────────────────────────────
// Supersedes b3ee685e's "everything else lives behind Details" for translation.* keys
// only (Guillaume, 2026-10-16): on a translation product the facts a reader picks on
// (device class, languages, offline, subscription) are the whole comparison.

export interface TranslationChip {
  text: string
  /** `translatorFirst` / `audioFirst` style the device-class chip; plain chips use the
   *  matched-spec pill look. */
  tone: 'translatorFirst' | 'audioFirst' | 'plain'
}

export interface TranslationGlance {
  chips: TranslationChip[]
  /** Muted conversation-modes line, or null when there is nothing to say. */
  modes: string | null
}

/** DealHunt conversation-mode token → reader label (mirrors DealHunt's pill labels). */
const CONVERSATION_MODE_LABELS: Record<string, string> = {
  listen: 'Listen',
  'face-to-face': 'Face-to-face',
  'shared earbuds': 'Share an earbud',
  speaker: 'Phone speaker',
  media: 'Calls & media',
  touch: 'Push-to-talk',
  group: 'Group',
}

function specBoolean(value: unknown): boolean | null {
  if (typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (v === 'true' || v === 'yes') {
      return true
    }
    if (v === 'false' || v === 'no') {
      return false
    }
  }
  return null
}

function specNumber(value: unknown): number | null {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null
  }
  const n = Number(value)
  return value !== '' && Number.isFinite(n) ? n : null
}

/** Array-valued specs arrive as `values` (set-valued chips) or as an array `value`. */
function specList(spec: DealHuntCompareSpec): string[] {
  const raw: unknown = spec.values ?? spec.value
  if (Array.isArray(raw)) {
    return raw.map(v => String(v).trim()).filter(Boolean)
  }
  if (typeof raw === 'string' && raw.trim()) {
    return raw
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
  }
  return []
}

/**
 * The card's translation row, derived from the hit's `compareSpecs` then `keySpecs` (first
 * occurrence of a key wins). Null when neither carries a `translation.*` key. Every chip
 * renders only when its key is present — DealHunt adds keys over time, never assume them.
 */
export function translationGlance(
  compareSpecs?: ReadonlyArray<DealHuntCompareSpec> | null,
  keySpecs?: ReadonlyArray<DealHuntCompareSpec> | null
): TranslationGlance | null {
  const byKey = new Map<string, DealHuntCompareSpec>()
  for (const spec of [...(compareSpecs ?? []), ...(keySpecs ?? [])]) {
    if (!byKey.has(spec.key)) {
      byKey.set(spec.key, spec)
    }
  }
  if (![...byKey.keys()].some(k => k.startsWith('translation.'))) {
    return null
  }
  const get = (key: string): DealHuntCompareSpec | undefined => byKey.get(key)
  const chips: TranslationChip[] = []

  const deviceClass = String(get('translation.device_class')?.value ?? '').toLowerCase()
  if (deviceClass === 'translator-first') {
    chips.push({ text: 'Translator-first', tone: 'translatorFirst' })
  } else if (deviceClass === 'audio-first') {
    chips.push({ text: 'Earbuds that translate', tone: 'audioFirst' })
  }

  const languages = specNumber(get('translation.languages_supported')?.value)
  if (languages !== null) {
    // The vendors say "100+"; DealHunt stores that claim as exactly 100.
    chips.push({
      text: languages === 100 ? '100+ languages' : `${languages} languages`,
      tone: 'plain',
    })
  }

  const accents = specNumber(get('translation.accents_supported')?.value)
  if (accents !== null) {
    chips.push({ text: `${accents} accents`, tone: 'plain' })
  }

  if (specBoolean(get('translation.has_offline_translation')?.value) === true) {
    const offlineLanguages = specNumber(get('translation.offline_languages')?.value)
    chips.push({
      text: offlineLanguages !== null ? `Offline · ${offlineLanguages} languages` : 'Offline',
      tone: 'plain',
    })
  }

  const subscription = specBoolean(get('features.requires_subscription')?.value)
  if (subscription !== null) {
    chips.push({ text: subscription ? 'Subscription' : 'No subscription', tone: 'plain' })
  }

  if (specBoolean(get('features.has_ai_transcription')?.value) === true) {
    chips.push({ text: 'AI transcription', tone: 'plain' })
  }

  const modesSpec = get('translation.conversation_modes')
  const modes = modesSpec
    ? specList(modesSpec).map(
        token =>
          CONVERSATION_MODE_LABELS[token.toLowerCase()] ??
          token.charAt(0).toUpperCase() + token.slice(1)
      )
    : []
  let modesLine: string | null = modes.length > 0 ? modes.join(' · ') : null
  if (
    modesLine === null &&
    deviceClass === 'audio-first' &&
    String(get('features.live_translation')?.value ?? '').toLowerCase() === 'in-app'
  ) {
    modesLine = 'Translation through the companion app'
  }

  if (chips.length === 0 && modesLine === null) {
    return null
  }
  return { chips, modes: modesLine }
}
